import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Save, Loader2, Sparkles, TrendingUp, Users } from 'lucide-react';
import BioInput from '@/components/shared/BioInput';
import { useEnhancedBioValidation } from '@/hooks/useEnhancedBioValidation';
import { BioValidationModal } from '@/components/onboarding/BioValidationModal';
import { BioWarningModal } from '@/components/onboarding/BioWarningModal';
import { BIO_MIN_LENGTH, ValidationRules } from '@/core/BusinessRulesEngine';

interface BioEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (bio: string, aiData?: { 
    topics?: string[],
    score?: number 
  }) => Promise<void>;
  initialBio: string;
  occupation?: string;
  education?: string;
}

export const BioEditModal: React.FC<BioEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialBio,
  occupation,
  education
}) => {
  const [tempBio, setTempBio] = useState(initialBio);
  const [isSaving, setIsSaving] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationModalType, setValidationModalType] = useState<'block' | 'warning'>('block');
  const [hasValidated, setHasValidated] = useState(false);

  const { isValidating, validationResult, validateBio, clearValidation } = useEnhancedBioValidation();

  // Reset temp bio when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempBio(initialBio);
      clearValidation();
      setHasValidated(false);
    }
  }, [isOpen, initialBio]);

  const handleBioChange = (newBio: string) => {
    setTempBio(newBio);
    clearValidation();
    setHasValidated(false);
  };

  const handleValidate = async () => {
    if (isValidating) return;
    
    const result = await validateBio(tempBio);
    
    if (!result.canProceed) {
      const modalType = ValidationRules.getBioSaveModalType(result.score);
      if (modalType === 'block') {
        setValidationModalType('block');
        setShowValidationModal(true);
        return;
      } else if (modalType === 'warning') {
        setValidationModalType('warning');
        setShowValidationModal(true);
        return;
      }
    }
    
    setHasValidated(true);
  };

  const handleSave = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const topics = validationResult?.topicsMentioned || [];
      const score = validationResult?.score ?? 0;

      await onSave(tempBio, {
        topics,
        score
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving bio:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const proceedWithSave = async () => {
    await handleSave();
  };

  const handleCancel = () => {
    setTempBio(initialBio);
    clearValidation();
    onClose();
  };

  const handleValidationModalClose = () => {
    setShowValidationModal(false);
  };

  const handleValidationModalProceed = async () => {
    setShowValidationModal(false);
    await proceedWithSave();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent 
        hasTitle
        className="w-screen h-screen max-w-none max-h-none m-0 rounded-none p-0 flex flex-col bg-black border-none"
      >
        <DialogHeader className="flex-shrink-0 px-6 py-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold text-white">
              Editar Biografia
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-4xl mx-auto space-y-4">
            <BioInput
              value={tempBio}
              onChange={handleBioChange}
              label=""
              placeholder="Conte sobre você, seus interesses, hobbies, profissão e o que mais achar importante..."
              maxLength={500}
              showQualityIndicator={false}
              showGuidance={false}
              showValidationIcon={false}
              occupation={occupation}
              education={education}
              className="min-h-[200px]"
              externalValidation={validationResult ? {
                ...validationResult,
                hasBeenValidated: !!validationResult,
                loading: isValidating
              } : undefined}
            />

            {/* ✨ Preview de Tópicos Extraídos pela IA */}
            {hasValidated && validationResult?.topicsMentioned && validationResult.topicsMentioned.length > 0 && (
              <div className="p-4 bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-lg border border-purple-500/30 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-purple-400" />
                  <h4 className="text-sm font-semibold text-white">Tópicos Extraídos pela IA</h4>
                  <Badge variant="secondary" className="ml-auto text-xs bg-purple-800/50 text-purple-200">
                    {validationResult.topicsMentioned.length} tópicos
                  </Badge>
                </div>
                
                <div className="space-y-3">
                  {/* Explicação do valor da IA */}
                  <div className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-white/90 leading-relaxed">
                        Nossa IA identificou <span className="font-semibold text-purple-300">
                        {validationResult.topicsMentioned.length} tópicos únicos</span> sobre você.
                        Esses tópicos são usados para conectar você com pessoas que compartilham
                        interesses similares.
                      </p>
                    </div>
                  </div>

                  {/* Mostrar só os TOP 5 tópicos mais relevantes */}
                  <div className="bg-purple-900/20 rounded-lg p-3 border border-purple-500/20">
                    <p className="text-xs text-purple-300 font-medium mb-2">
                      💡 Principais tópicos detectados:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {validationResult.topicsMentioned.slice(0, 5).map((topic, idx) => (
                        <Badge 
                          key={`${topic}-${idx}`} 
                          variant="outline" 
                          className="text-xs bg-purple-900/40 border-purple-500/40 text-purple-200"
                        >
                          {topic}
                        </Badge>
                      ))}
                      {validationResult.topicsMentioned.length > 5 && (
                        <span className="text-xs text-purple-300/70">
                          e mais {validationResult.topicsMentioned.length - 5} tópicos
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Estatística motivacional */}
                  <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-lg p-3 border border-purple-500/20">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-400" />
                      <p className="text-xs text-white/80">
                        <span className="font-semibold text-green-400">
                          Perfis detalhados
                        </span> recebem 3x mais matches relevantes
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-white/10 bg-black/20">
          <div className="max-w-4xl mx-auto flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              className="bg-red-800 hover:bg-red-700 text-white border-red-700 hover:border-red-600"
            >
              Cancelar
            </Button>
            <Button
              onClick={hasValidated ? handleSave : handleValidate}
              disabled={tempBio.trim().length < BIO_MIN_LENGTH || isSaving || isValidating}
              className={hasValidated 
                ? "bg-emerald-800 hover:bg-emerald-700 text-white disabled:bg-gray-600 disabled:text-gray-300" 
                : "bg-purple-800 hover:bg-purple-700 text-white disabled:bg-gray-600 disabled:text-gray-300"
              }
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Salvando...
                </>
              ) : isValidating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Validando com IA...
                </>
              ) : hasValidated ? (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Salvar
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Validar com IA
                </>
              )}
            </Button>
          </div>
        </div>

        <BioValidationModal 
          isOpen={showValidationModal}
          onClose={handleValidationModalClose}
          onProceed={handleValidationModalProceed}
          bioScore={validationResult?.score || 0}
          modalType={validationModalType}
          feedback={validationResult?.feedback}
        />

        <BioWarningModal 
          isOpen={showValidationModal && validationModalType === 'warning'}
          onClose={handleValidationModalClose}
          onProceed={handleValidationModalProceed}
          bioScore={validationResult?.score || 0}
          feedback={validationResult?.feedback}
        />
      </DialogContent>
    </Dialog>
  );
};
